import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CHANNEL_STATUS } from '@/entities/channel';
import { useExternalChannelById } from '@/features/channels';
import { Loader } from '@/shared/ui/Loader';
import { Logo } from '@/shared/ui/Logo';
import { MaxWidthContainer } from '@/shared/ui/PageContainer';
import { QrCode } from '@/shared/ui/QrCode';
import { CustomTypography } from '@/shared/ui/Typography';

import { StyledBox, StyledText, StyledWrapper } from './styles';
import { TGExternalPasswordRenderer } from './TGExternalPassordRenderer';

const getIsFinalStatus = (status?: string) => {
  return (
    status === CHANNEL_STATUS.AUTHORIZED ||
    status === CHANNEL_STATUS.READY ||
    status === CHANNEL_STATUS.CONTACTS_SYNCED ||
    status === CHANNEL_STATUS.CHATS_SYNCED
  );
};

export const ShareQrPage: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const {
    channelByIdData,
    isError: isErrorChannel,
    isLoading,
    refetch,
  } = useExternalChannelById(id, {
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state?.data?.status;

      if (getIsFinalStatus(status) || query.state?.error) {
        return false;
      }

      return 10000;
    },
  });

  if (isLoading) {
    return (
      <MaxWidthContainer>
        <Logo sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} />
        <StyledWrapper>
          <Loader containerSize="large" />
        </StyledWrapper>
      </MaxWidthContainer>
    );
  }

  if (isErrorChannel) {
    return (
      <MaxWidthContainer>
        <Logo sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} />
        <StyledWrapper>
          <StyledBox>
            <StyledText>
              <CustomTypography sx={{ fontSize: '24px' }} color="error.main">
                {t('common:shareQrPage.qrNotFound')}
              </CustomTypography>
            </StyledText>
          </StyledBox>
        </StyledWrapper>
      </MaxWidthContainer>
    );
  }

  if (getIsFinalStatus(channelByIdData?.status)) {
    return (
      <MaxWidthContainer>
        <Logo sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} />
        <StyledWrapper>
          <StyledBox>
            <StyledText>
              <CustomTypography sx={{ fontSize: '16px' }} color="primary">
                {t('common:shareQrPage.success')}
              </CustomTypography>
            </StyledText>
          </StyledBox>
        </StyledWrapper>
      </MaxWidthContainer>
    );
  }

  if (channelByIdData?.status === CHANNEL_STATUS.PASSWORD_REQUIRED) {
    return (
      <MaxWidthContainer>
        <Logo sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} />
        <StyledWrapper>
          <StyledBox>
            <StyledText>
              <TGExternalPasswordRenderer
                id={id ?? ''}
                refetch={() => refetch()}
              />
            </StyledText>
          </StyledBox>
        </StyledWrapper>
      </MaxWidthContainer>
    );
  }

  return (
    <MaxWidthContainer>
      <Logo sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }} />
      <StyledWrapper>
        <StyledBox>
          <StyledText>
            <div>{t('common:shareQrPage.scanQr')}</div>
            <QrCode qrData={channelByIdData?.qr_url} />
            <CustomTypography
              sx={{ fontSize: '16px', mt: 2, color: 'text.secondary' }}
            >
              {t('common:shareQrPage.instructions')}
            </CustomTypography>
          </StyledText>
        </StyledBox>
      </StyledWrapper>
    </MaxWidthContainer>
  );
};
